import {Injectable, TemplateRef, Type} from '@angular/core';
import { Subject } from 'rxjs';

export interface ModalConfig {
  panelClass?: string | string[];
  width?: string;
  height?: string;
  hideCloseBtn?: boolean;
  blurBackdrop?: boolean;
  backdrop?: boolean | 'static';
  keyboard?: boolean;
  centered?: boolean;
  windowClass?: string;
  backdropClass?: string;
  blurAmount?: string;
  [key: string]: any;
}

export type ModalOptions = ModalConfig;

const DEFAULT_MODAL_CONFIG: ModalConfig = {
  width: 'w-full max-w-lg',
  blurBackdrop: false
};

export class ModalRef {
  result: Promise<any>;
  componentInstance: any = {};
  private resolve!: (value?: any) => void;

  constructor() {
    this.result = new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  close(result?: any) {
    this.resolve(result);
  }
}

export interface ModalEvent {
  component: Type<any> | TemplateRef<any>;
  data?: any;
  config?: ModalConfig;
  ref: ModalRef;
}

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private openSubject = new Subject<ModalEvent>();
  private closeSubject = new Subject<void>();
  private activeModalRef: ModalRef | null = null;

  open$ = this.openSubject.asObservable();
  close$ = this.closeSubject.asObservable();

  open(component: Type<any> | TemplateRef<any>, data?: any, config?: ModalConfig): ModalRef {
    const finalConfig = { ...DEFAULT_MODAL_CONFIG, ...config };
    const ref = new ModalRef();
    this.activeModalRef = ref;
    this.openSubject.next({ component, data, config: finalConfig, ref });
    return ref;
  }

  isModalOpen(): boolean {
    return this.activeModalRef !== null;
  }

  openModal(component: Type<any> | TemplateRef<any>, size: any = 'md', options?: any): ModalRef {
    const width = typeof size === 'string' ? size : 'md'; // Keep it simple
    const config = { width, ...options };
    return this.open(component, options?.data, config);
  }

  openModalWithSingleData(component: Type<any> | TemplateRef<any>, size: any = 'lg', data: any, options?: any): ModalRef {
    const width = typeof size === 'string' ? size : 'lg';
    const config = { width, ...options };
    // Need to pass data so the overlay component injects it correctly
    return this.open(component, data, config);
  }

  closeModal(result?: any) {
    this.close(result);
  }

  close(result?: any) {
    if (this.activeModalRef) {
      this.activeModalRef.close(result);
      this.activeModalRef = null;
    }
    this.closeSubject.next();
  }
}
